import React from 'react';
import { distinctSystemName } from './demoEnvironmentStatus';
import { clickedAControl } from './agencyDataFillView';

/**
 * The agencies, as a list you pick from rather than a table you read across.
 *
 * What was four columns is one row of two lines: who the agency is, and the
 * one-glance state. The dates and the detail belong to the pane beside it,
 * which is where the agency they describe already is.
 *
 * Two screens draw this list -- Agency Data Fill, and Demo Sandboxes -- and
 * they differ only in what the badge and the second line say. Everything that
 * decides how the list behaves lives here, so picking an agency, the keyboard
 * contract and the selected row look the same on both.
 */

export interface AgencyRailRow {
  agencyId: string;
  /** The agency's own name where it has one, else the system name. */
  displayName: string;
  /** Kept so the rail can decide whether to show the system name beside it. */
  writtenName: string | null;
  systemName: string;
  /** Dimmed, for a row that offers nothing to do. */
  muted?: boolean;
  /** Appended to the row's accessible name when it is muted. */
  mutedNote?: string;
}

export function AgencyRail<Row extends AgencyRailRow>({
  rows,
  selectedId,
  onSelect,
  badge,
  state,
}: {
  rows: readonly Row[];
  selectedId: string | null;
  onSelect: (agencyId: string) => void;
  /** The icon at the right of the name line, if the screen has one. */
  badge?: (row: Row) => React.ReactNode;
  /** The one line under the name: this screen's one-glance state. */
  state: (row: Row) => React.ReactNode;
}) {
  return (
    /* Not a listbox. That role promises arrow-key navigation between options,
       and this list does not implement it -- announcing one and ignoring Down
       is worse than announcing nothing. Rows are buttons, so the keyboard
       contract is the one they keep. */
    <div aria-label='Agencies' className='min-h-0 flex-1 overflow-y-auto'>
      {rows.map(row => {
        const name = row.displayName;
        const systemName = distinctSystemName(row.writtenName, row.systemName);
        const selected = row.agencyId === selectedId;
        return (
          <div
            key={row.agencyId}
            role='button'
            aria-pressed={selected}
            /* The row's own text names it. A muted row gets the state appended
               rather than replacing that text, so the name is still
               announced. */
            aria-label={
              row.muted && row.mutedNote
                ? `${name} — ${row.mutedNote}`
                : undefined
            }
            tabIndex={0}
            /* focus-visible, or a keyboard user tabs into a row with nothing
               saying which one they are on. */
            className={`cursor-pointer border-b px-3 py-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset ${
              selected
                ? 'border-l-[3px] border-l-primary bg-muted pl-[calc(0.75rem-3px)]'
                : 'hover:bg-muted/50'
            } ${row.muted ? 'text-muted-foreground' : ''}`}
            onClick={event => {
              if (clickedAControl(event.target, event.currentTarget)) return;
              onSelect(row.agencyId);
            }}
            onKeyDown={event => {
              if (event.target !== event.currentTarget) return;
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onSelect(row.agencyId);
              }
            }}
          >
            <div className='flex items-baseline justify-between gap-2'>
              {/* Name and system name on one line rather than two. The system
                  name only shows when it differs from the written one, so most
                  rows are one word and the rail holds half again as many. */}
              <div className='flex min-w-0 items-center gap-1.5'>
                <span className='truncate text-[13px] font-medium' title={name}>
                  {name}
                </span>
                {systemName && (
                  <span
                    className='shrink-0 truncate font-mono text-[11px] text-muted-foreground'
                    title={systemName}
                  >
                    {systemName}
                  </span>
                )}
              </div>
              {badge?.(row)}
            </div>

            <div className='truncate text-[11px] text-muted-foreground'>
              {state(row)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default AgencyRail;
